import { z } from 'zod';
import {
  TaxReliefElegibleType,
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import isMongoId from '../../helpers/is-mongo-id';
import { withPaginationParamsOptional } from '../../helpers';

export const SumSharesHeldOnDateInput = z.object({
  firmId: z.string().refine(isMongoId()),
  exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
    message: 'Exit date must be in DD/MM/YYYY format',
  }),
  companyIds: z.array(z.string().refine(isMongoId())).min(1),
  shareAllocationUploadIds: z.array(z.string().refine(isMongoId())),
  exitId: z.string().refine(isMongoId()).optional(),
});

export const StageExitForCreationInput = z
  .object({
    firmId: z.string().refine(isMongoId()),
    exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
      message: 'Exit date must be in DD/MM/YYYY format',
    }),
    companyIds: z
      .array(
        z.string().refine(isMongoId(), {
          message: 'Company ID must be a valid MongoDB ID',
        }),
      )
      .min(1, { message: 'At least one company ID is required' }),
    sharesToBeSold: z
      .number({ required_error: 'Shares to be sold is required' })
      .min(0, {
        message: 'Shares to be sold must be 0 or greater',
      }),
    sharePrice: z.number({ required_error: 'Share price is required' }).min(0, {
      message: 'Share price must be 0 or greater',
    }),
    taxableValueSharePrice: z.number({ required_error: 'Taxable value share price is required' }).min(0, {
      message: 'Taxable value share price must be 0 or greater',
    }),
    shareAllocationUploadIds: z.array(
      z.string().refine(isMongoId(), {
        message: 'Share allocation upload ID must be a valid MongoDB ID',
      }),
      { required_error: 'Share allocation upload IDs are required' },
    ),
    considerationType: z.nativeEnum(ConsiderationType, {
      required_error: 'Consideration type is required',
      invalid_type_error:
        'Consideration type must be a valid ConsiderationType enum value',
    }),
    shareConsiderationCompanyId: z
      .string()
      .refine(isMongoId(), {
        message: 'Share consideration company ID must be a valid MongoDB ID',
      })
      .optional(),
    shareConsiderationShareClassId: z
      .string()
      .refine(isMongoId(), {
        message:
          'Share consideration share class ID must be a valid MongoDB ID',
      })
      .optional(),
    totalConsiderationShares: z
      .number({
        invalid_type_error: 'Total consideration shares must be a number',
      })
      .min(0, { message: 'Total consideration shares must be 0 or greater' })
      .optional(),
    shareConsiderationValuePerShare: z
      .number({
        invalid_type_error:
          'Share consideration value per share must be a number',
      })
      .min(0, {
        message: 'Share consideration value per share must be 0 or greater',
      })
      .optional(),
    shareConsiderationValueTotal: z
      .number({
        invalid_type_error: 'Share consideration value total must be a number',
      })
      .min(0, {
        message: 'Share consideration value total must be 0 or greater',
      })
      .optional(),
    shareConsiderationEisEligible: z
      .boolean({
        invalid_type_error:
          'Share consideration EIS eligible must be a boolean',
      })
      .optional(),
    shareConsiderationAllocationMethod: z
      .nativeEnum(ShareConsiderationAllocationMethod, {
        invalid_type_error:
          'Share consideration allocation method must be a valid ShareConsiderationAllocationMethod enum value',
      })
      .optional(),
    permitPartialShareAllocation: z
      .boolean({
        invalid_type_error: 'Permit partial share allocation must be a boolean',
      })
      .optional(),
    maintainOriginalExitDetails: z
      .boolean({
        invalid_type_error: 'Maintain original exit details must be a boolean',
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationCompanyId !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration company ID is required for share consideration',
      path: ['shareConsiderationCompanyId'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationShareClassId !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration share class ID is required for share consideration',
      path: ['shareConsiderationShareClassId'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.totalConsiderationShares !== undefined;
      }
      return true;
    },
    {
      message: 'Total consideration shares is required for share consideration',
      path: ['totalConsiderationShares'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationValuePerShare !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration value per share is required for share consideration',
      path: ['shareConsiderationValuePerShare'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationValueTotal !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration value total is required for share consideration',
      path: ['shareConsiderationValueTotal'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationEisEligible !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration EIS eligible is required for share consideration',
      path: ['shareConsiderationEisEligible'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationAllocationMethod !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration allocation method is required for share consideration',
      path: ['shareConsiderationAllocationMethod'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.permitPartialShareAllocation !== undefined;
      }
      return true;
    },
    {
      message:
        'Permit partial share allocation is required for share consideration',
      path: ['permitPartialShareAllocation'],
    },
  )
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const StageExitForEditInput = z.object({
  firmId: z.string().refine(isMongoId()),
  exitId: z.string().refine(isMongoId()),
  exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
    message: 'Exit date must be in DD/MM/YYYY format',
  }),
  sharePrice: z.number().min(0),
  taxableValueSharePrice: z.number().min(0),
  isReceivedValueSharePriceUnchanged: z.boolean(),
  considerationType: z.nativeEnum(ConsiderationType).optional(),
  shareConsiderationCompanyId: z
    .string()
    .refine(isMongoId(), {
      message: 'Share consideration company ID must be a valid MongoDB ID',
    })
    .optional(),
  shareConsiderationShareClassId: z
    .string()
    .refine(isMongoId(), {
      message: 'Share consideration share class ID must be a valid MongoDB ID',
    })
    .optional(),
  totalConsiderationShares: z
    .number({
      invalid_type_error: 'Total consideration shares must be a number',
    })
    .min(0, { message: 'Total consideration shares must be 0 or greater' })
    .optional(),
  shareConsiderationValuePerShare: z
    .number({
      invalid_type_error:
        'Share consideration value per share must be a number',
    })
    .min(0, {
      message: 'Share consideration value per share must be 0 or greater',
    })
    .optional(),
  shareConsiderationValueTotal: z
    .number({
      invalid_type_error: 'Share consideration value total must be a number',
    })
    .min(0, {
      message: 'Share consideration value total must be 0 or greater',
    })
    .optional(),
  shareConsiderationEisEligible: z
    .boolean({
      invalid_type_error: 'Share consideration EIS eligible must be a boolean',
    })
    .optional(),
  shareConsiderationAllocationMethod: z
    .nativeEnum(ShareConsiderationAllocationMethod, {
      invalid_type_error:
        'Share consideration allocation method must be a valid ShareConsiderationAllocationMethod enum value',
    })
    .optional(),
  permitPartialShareAllocation: z
    .boolean({
      invalid_type_error: 'Permit partial share allocation must be a boolean',
    })
    .optional(),
  maintainOriginalExitDetails: z
    .boolean({
      invalid_type_error: 'Maintain original exit details must be a boolean',
    })
    .optional(),
})
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

// For correcting original exits - similar to StageExitForCreationInput but requires exitId
export const StageExitForCorrectionInput = z
  .object({
    exitId: z.string().refine(isMongoId()), // Required for corrections
    exitAmendmentId: z.string().refine(isMongoId()).optional(), // The specific amendment being corrected
    firmId: z.string().refine(isMongoId()),
    exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
      message: 'Exit date must be in DD/MM/YYYY format',
    }),
    companyIds: z
      .array(
        z.string().refine(isMongoId(), {
          message: 'Company ID must be a valid MongoDB ID',
        }),
      )
      .min(1, { message: 'At least one company ID is required' }),
    sharesToBeSold: z
      .number({ required_error: 'Shares to be sold is required' })
      .min(0, {
        message: 'Shares to be sold must be 0 or greater',
      }),
    sharePrice: z.number({ required_error: 'Share price is required' }).min(0, {
      message: 'Share price must be 0 or greater',
    }),
    taxableValueSharePrice: z.number({ required_error: 'Taxable value share price is required' }).min(0, {
      message: 'Taxable value share price must be 0 or greater',
    }),
    shareAllocationUploadIds: z.array(
      z.string().refine(isMongoId(), {
        message: 'Share allocation upload ID must be a valid MongoDB ID',
      }),
      { required_error: 'Share allocation upload IDs are required' },
    ),
    considerationType: z.nativeEnum(ConsiderationType, {
      required_error: 'Consideration type is required',
      invalid_type_error:
        'Consideration type must be a valid ConsiderationType enum value',
    }),
    shareConsiderationCompanyId: z
      .string()
      .refine(isMongoId(), {
        message: 'Share consideration company ID must be a valid MongoDB ID',
      })
      .optional(),
    shareConsiderationShareClassId: z
      .string()
      .refine(isMongoId(), {
        message:
          'Share consideration share class ID must be a valid MongoDB ID',
      })
      .optional(),
    totalConsiderationShares: z
      .number({
        invalid_type_error: 'Total consideration shares must be a number',
      })
      .min(0, { message: 'Total consideration shares must be 0 or greater' })
      .optional(),
    shareConsiderationValuePerShare: z
      .number({
        invalid_type_error:
          'Share consideration value per share must be a number',
      })
      .min(0, {
        message: 'Share consideration value per share must be 0 or greater',
      })
      .optional(),
    shareConsiderationValueTotal: z
      .number({
        invalid_type_error: 'Share consideration value total must be a number',
      })
      .min(0, {
        message: 'Share consideration value total must be 0 or greater',
      })
      .optional(),
    shareConsiderationEisEligible: z
      .boolean({
        invalid_type_error:
          'Share consideration EIS eligible must be a boolean',
      })
      .optional(),
    shareConsiderationAllocationMethod: z
      .nativeEnum(ShareConsiderationAllocationMethod, {
        invalid_type_error:
          'Share consideration allocation method must be a valid ShareConsiderationAllocationMethod enum value',
      })
      .optional(),
    permitPartialShareAllocation: z
      .boolean({
        invalid_type_error: 'Permit partial share allocation must be a boolean',
      })
      .optional(),
    maintainOriginalExitDetails: z
      .boolean({
        invalid_type_error: 'Maintain original exit details must be a boolean',
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationCompanyId !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration company ID is required for share consideration',
      path: ['shareConsiderationCompanyId'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationShareClassId !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration share class ID is required for share consideration',
      path: ['shareConsiderationShareClassId'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.totalConsiderationShares !== undefined;
      }
      return true;
    },
    {
      message: 'Total consideration shares is required for share consideration',
      path: ['totalConsiderationShares'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationValuePerShare !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration value per share is required for share consideration',
      path: ['shareConsiderationValuePerShare'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationValueTotal !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration value total is required for share consideration',
      path: ['shareConsiderationValueTotal'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationEisEligible !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration EIS eligible is required for share consideration',
      path: ['shareConsiderationEisEligible'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.shareConsiderationAllocationMethod !== undefined;
      }
      return true;
    },
    {
      message:
        'Share consideration allocation method is required for share consideration',
      path: ['shareConsiderationAllocationMethod'],
    },
  )
  .refine(
    (data) => {
      if (data.considerationType === ConsiderationType.Share) {
        return data.permitPartialShareAllocation !== undefined;
      }
      return true;
    },
    {
      message:
        'Permit partial share allocation is required for share consideration',
      path: ['permitPartialShareAllocation'],
    },
  )
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

// For correcting amendments - similar to StageExitForEditInput but adds optional exitAmendmentId and makes maintainOriginalExitDetails optional; exitAmendmentId is unused in staging.
export const StageExitAmendmentForCorrectionInput = z.object({
  exitId: z.string().refine(isMongoId()),
  exitAmendmentId: z
    .string()
    .refine(
      (id) => id === undefined || (id !== '' && isMongoId()(id)),
      {
        message: 'Exit amendment ID must be a valid MongoDB ID when provided',
      },
    )
    .optional(),
  firmId: z.string().refine(isMongoId()),
  exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
    message: 'Exit date must be in DD/MM/YYYY format',
  }),
  sharePrice: z.number().min(0),
  taxableValueSharePrice: z.number().min(0),
  isReceivedValueSharePriceUnchanged: z.boolean(),
  considerationType: z.nativeEnum(ConsiderationType).optional(),
  shareConsiderationCompanyId: z
    .string()
    .refine(isMongoId(), {
      message: 'Share consideration company ID must be a valid MongoDB ID',
    })
    .optional(),
  shareConsiderationShareClassId: z
    .string()
    .refine(isMongoId(), {
      message: 'Share consideration share class ID must be a valid MongoDB ID',
    })
    .optional(),
  totalConsiderationShares: z
    .number({
      invalid_type_error: 'Total consideration shares must be a number',
    })
    .min(0, { message: 'Total consideration shares must be 0 or greater' })
    .optional(),
  shareConsiderationValuePerShare: z
    .number({
      invalid_type_error:
        'Share consideration value per share must be a number',
    })
    .min(0, {
      message: 'Share consideration value per share must be 0 or greater',
    })
    .optional(),
  shareConsiderationValueTotal: z
    .number({
      invalid_type_error: 'Share consideration value total must be a number',
    })
    .min(0, {
      message: 'Share consideration value total must be 0 or greater',
    })
    .optional(),
  shareConsiderationEisEligible: z
    .boolean({
      invalid_type_error: 'Share consideration EIS eligible must be a boolean',
    })
    .optional(),
  shareConsiderationAllocationMethod: z
    .nativeEnum(ShareConsiderationAllocationMethod, {
      invalid_type_error:
        'Share consideration allocation method must be a valid ShareConsiderationAllocationMethod enum value',
    })
    .optional(),
  permitPartialShareAllocation: z
    .boolean({
      invalid_type_error: 'Permit partial share allocation must be a boolean',
    })
    .optional(),
  maintainOriginalExitDetails: z
    .boolean({
      invalid_type_error: 'Maintain original exit details must be a boolean',
    })
    .optional(),
})
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const CalculatePerformanceFeeInput = z.object({
  shareholdingId: z.string().refine(isMongoId()),
  preFetchedShareholding: z.any().optional(),
  firmId: z.string().refine(isMongoId()).optional(),
  totalSaleValue: z.number(),
});

const ShareholdingToBeExitedSchema = z.object({
  shareholdingId: z.string().refine(isMongoId()),
  sharesAllocatedForSale: z.number(),
  totalSaleValue: z.number(),
  taxableValue: z.number(),
  performanceFee: z.number(),
  sharePrice: z.number().min(0),
  accruedFeesCharged: z.number().optional(),
  additionalGrossReceivedValue: z.number().optional(),
});

const ShareConsiderationToBeExitedSchema = z.object({
  shareholdingId: z.string().refine(isMongoId()),
  sharesAllocatedForSale: z.number(),
  newSharesAllocated: z.number().min(0),
});

export const CreateExitInput = z
  .object({
    exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
      message: 'Exit date must be in DD/MM/YYYY format',
    }),
    firmId: z.string().refine(isMongoId()),
    sharePrice: z.number().min(0),
    taxableValueSharePrice: z.number().min(0),
    taxReliefElegible: z.nativeEnum(TaxReliefElegibleType),
    taxReliefElegibleAllocationIds: z.array(z.string().refine(isMongoId())),
    companyIds: z.array(z.string().refine(isMongoId())).min(1),
    shareholdingsToBeExited: z.array(ShareholdingToBeExitedSchema).min(1),
    shareAllocationUploadIds: z.array(z.string().refine(isMongoId())),
  })
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const CreateShareConsiderationExitInput = z.object({
  exitDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
    message: 'Exit date must be in DD/MM/YYYY format',
  }),
  firmId: z.string().refine(isMongoId()),
  sharePrice: z.number().min(0),
  taxableValueSharePrice: z.number().min(0),
  taxReliefElegible: z.nativeEnum(TaxReliefElegibleType),
  taxReliefElegibleAllocationIds: z.array(z.string().refine(isMongoId())),
  companyIds: z.array(z.string().refine(isMongoId())).min(1),
  shareConsiderationsToBeExited: z
    .array(ShareConsiderationToBeExitedSchema)
    .min(1),
  shareAllocationUploadIds: z.array(z.string().refine(isMongoId())),
  shareConsiderationCompanyId: z.string().refine(isMongoId()),
  shareConsiderationShareClassId: z.string().refine(isMongoId()),
  shareConsiderationValuePerShare: z.number().min(0),
  totalConsiderationShares: z.number().min(0),
  shareConsiderationEisEligible: z.boolean(),
});

export const EditExitInput = z
  .object({
    exitId: z.string().refine(isMongoId()),
    firmId: z.string().refine(isMongoId()),
    exitAmendmentDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, {
      message: 'Exit amendment date must be in DD/MM/YYYY format',
    }),
    sharePrice: z.number().min(0),
    taxableValueSharePrice: z.number().min(0),
    shareholdingsToBeExited: z
      .array(
        ShareholdingToBeExitedSchema.extend({
          additionalGrossReceivedValue: z.number().min(0),
        }),
      )
      .min(1),
    taxReliefElegible: z.nativeEnum(TaxReliefElegibleType),
    taxReliefElegibleAllocationIds: z.array(z.string().refine(isMongoId())),
  })
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const EditShareConsiderationExitInput = z.object({
  exitId: z
    .string({ required_error: 'Exit ID is required' })
    .refine(isMongoId(), {
      message: 'Exit ID must be a valid MongoDB ID',
    }),
  exitAmendmentId: z
    .string()
    .refine(isMongoId(), {
      message: 'Exit amendment ID must be a valid MongoDB ID',
    })
    .optional(),
  firmId: z
    .string({ required_error: 'Firm ID is required' })
    .refine(isMongoId(), {
      message: 'Firm ID must be a valid MongoDB ID',
    }),
  exitAmendmentDate: z
    .string({ required_error: 'Exit amendment date is required' })
    .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
      message: 'Exit amendment date must be in DD/MM/YYYY format',
    }),
  sharePrice: z.number({ required_error: 'Share price is required' }).min(0, {
    message: 'Share price must be 0 or greater',
  }),
  taxableValueSharePrice: z
    .number({ required_error: 'Taxable value share price is required' })
    .min(0, {
      message: 'Taxable value share price must be 0 or greater',
    }),
  shareConsiderationsToBeExited: z
    .array(ShareConsiderationToBeExitedSchema, {
      required_error: 'Share considerations to be exited is required',
    })
    .min(1, {
      message: 'At least one share consideration must be provided',
    }),
  taxReliefElegible: z.nativeEnum(TaxReliefElegibleType, {
    required_error: 'Tax relief eligible is required',
    invalid_type_error:
      'Tax relief eligible must be a valid TaxReliefElegibleType enum value',
  }),
  taxReliefElegibleAllocationIds: z.array(
    z.string().refine(isMongoId(), {
      message: 'Tax relief eligible allocation ID must be a valid MongoDB ID',
    }),
    { required_error: 'Tax relief eligible allocation IDs is required' },
  ),
  shareConsiderationValuePerShare: z
    .number({
      required_error: 'Share consideration value per share is required',
    })
    .min(0, {
      message: 'Share consideration value per share must be 0 or greater',
    }),
  shareConsiderationShareClassId: z
    .string({
      required_error: 'Share consideration share class ID is required',
    })
    .min(1, {
      message: 'Share consideration share class ID is required',
    })
    .refine(isMongoId(), {
      message: 'Share consideration share class ID must be a valid MongoDB ID',
    }),
  isInitialConsideration: z.boolean().optional(),
});

export const CorrectExitInput = z
  .object({
    exitId: z.string().refine(isMongoId()),
    exitAmendmentId: z.string().refine(isMongoId()).optional(),
    firmId: z.string().refine(isMongoId()),
    exitDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'Exit date must be in DD/MM/YYYY format',
      })
      .optional(),
    exitAmendmentDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'Exit amendment date must be in DD/MM/YYYY format',
      })
      .optional(),
    sharePrice: z.number().min(0),
    taxableValueSharePrice: z.number().min(0),
    shareholdingsToBeExited: z
      .array(
        ShareholdingToBeExitedSchema.extend({
          additionalGrossReceivedValue: z.number().min(0),
        }),
      )
      .min(1),
    taxReliefElegible: z.nativeEnum(TaxReliefElegibleType),
    taxReliefElegibleAllocationIds: z.array(z.string().refine(isMongoId())),
    companyIds: z.array(z.string().refine(isMongoId())).optional(),
    shareAllocationUploadIds: z.array(z.string().refine(isMongoId())).optional(),
    maintainOriginalExitDetails: z.boolean().optional(),
  })
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const CorrectShareConsiderationExitInput = z
  .object({
    exitId: z.string().refine(isMongoId()),
    exitAmendmentId: z.string().refine(isMongoId()).optional(),
    firmId: z.string().refine(isMongoId()),
    exitDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'Exit date must be in DD/MM/YYYY format',
      })
      .optional(),
    exitAmendmentDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'Exit amendment date must be in DD/MM/YYYY format',
      })
      .optional(),
    sharePrice: z.number().min(0),
    taxableValueSharePrice: z.number().min(0),
    shareConsiderationsToBeExited: z
      .array(ShareConsiderationToBeExitedSchema)
      .min(1),
    taxReliefElegible: z.nativeEnum(TaxReliefElegibleType),
    taxReliefElegibleAllocationIds: z.array(z.string().refine(isMongoId())),
    shareConsiderationValuePerShare: z.number().min(0),
    shareConsiderationShareClassId: z.string().refine(isMongoId()),
    companyIds: z.array(z.string().refine(isMongoId())).optional(),
    shareAllocationUploadIds: z.array(z.string().refine(isMongoId())).optional(),
    shareConsiderationCompanyId: z.string().refine(isMongoId()).optional(),
    totalConsiderationShares: z.number().min(0).optional(),
    shareConsiderationEisEligible: z.boolean().optional(),
    isInitialConsideration: z.boolean().optional(),
    maintainOriginalExitDetails: z.boolean().optional(),
  })
  .refine((data) => data.taxableValueSharePrice >= data.sharePrice, {
    message: 'Taxable value cannot be less than share price',
    path: ['taxableValueSharePrice'],
  });

export const DeleteExitInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const DeleteExitAmendmentInput = z.object({
  id: z.string().refine(isMongoId()),
  exitAmendmentId: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const CanDeleteExitInput = z.object({
  id: z.string().refine(isMongoId()),
  exitAmendmentId: z.string().refine(isMongoId()).optional(),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const ListExitsInput = withPaginationParamsOptional(
  z.object({
    companyId: z.string().refine(isMongoId()).optional(),
    firmId: z.string().refine(isMongoId()).optional(),
    investorId: z.string().refine(isMongoId()).optional(),
    startDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'Start date must be in DD/MM/YYYY format',
      })
      .optional(),
    endDate: z
      .string()
      .regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: 'End date must be in DD/MM/YYYY format',
      })
      .optional(),
    companyName: z.string().optional(),
  }),
  ['date'],
);

export const GetExitByIdInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const GetConsiderationHistoryInput = z.object({
  exitId: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const BulkExportPdfExitStatementsInput = z.object({
  exitStatementId: z.string().refine(isMongoId()),
  selectedInvestors: z.array(
    z.object({
      _id: z.string().refine(isMongoId()),
      fullName: z.string(),
    }),
  ),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const DownloadExitsInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});
